/**
 * Personalized Bundle System
 * Fetches customer's owned products and adjusts bundle pricing/display accordingly
 */ 

(function() {
  'use strict';

  // Cache for owned products data
  let ownedProductsCache = null;
  let cacheTimestamp = null;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch customer's owned products from the serials API
   */
  async function fetchOwnedProducts() {
    // Check cache first
    if (ownedProductsCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      console.log('[Personalized Bundles] Using cached owned products');
      return ownedProductsCache;
    }

    // Get customer ID from window (set by Liquid)
    const customerId = window.Shopify?.customer?.id || window.shopifyCustomerId;
    
    if (!customerId) {
      console.log('[Personalized Bundles] No customer ID available');
      return { serialCodes: [] };
    }

    try {
      const response = await fetch(`/apps/serials?shopifyUserID=${customerId}`, {
        credentials: 'same-origin'
      });

      if (!response.ok) {
        console.warn('[Personalized Bundles] Failed to fetch owned products:', response.status);
        return { serialCodes: [] };
      }

      const data = await response.json();
      console.log('[Personalized Bundles] Fetched serials data:', data);

      // Cache the result
      ownedProductsCache = data;
      cacheTimestamp = Date.now();

      return data;
    } catch (error) {
      console.error('[Personalized Bundles] Error fetching owned products:', error);
      return { serialCodes: [] };
    }
  }

  /**
   * Get array of owned product IDs and handles
   */
  async function getOwnedProductIdentifiers() {
    const data = await fetchOwnedProducts();
    
    if (!data.serialCodes || data.serialCodes.length === 0) {
      return { ids: [], handles: [] };
    }

    const handles = new Set();
    const ids = new Set();

    // Extract unique product slugs from serial codes
    data.serialCodes.forEach(serial => {
      if (serial.productSlug) {
        handles.add(serial.productSlug);
      }
    });

    console.log('[Personalized Bundles] Found owned product handles:', Array.from(handles));

    return { 
      ids: Array.from(ids), 
      handles: Array.from(handles) 
    };
  }

  /**
   * Check if a product is owned by the customer
   */
  async function isProductOwned(productHandle) {
    const { handles } = await getOwnedProductIdentifiers();
    return productHandle && handles.includes(productHandle);
  }

  /**
   * Process bundle items and mark which are owned
   */
  async function processBundleItems(bundleItems) {
    const { handles } = await getOwnedProductIdentifiers();
    
    return bundleItems.map(item => {
      const isOwned = item.handle && handles.includes(item.handle);
      
      if (isOwned) {
        console.log('[Personalized Bundles] Item is owned:', item.handle);
      }
      
      return {
        ...item,
        isOwned: isOwned
      };
    });
  }

  /**
   * Calculate personalized bundle pricing
   * Matches Shopify's cart calculation by applying discount to each item individually
   */
  function calculatePersonalizedPricing(bundleItems, bundlePrice, discountPercentageOverride = null) {
    const ownedItems = bundleItems.filter(item => item.isOwned);
    const unownedItems = bundleItems.filter(item => !item.isOwned);

    // Calculate original subtotal
    const fullSubtotal = bundleItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return sum + (price * quantity);
    }, 0);

    // Use the discount percentage from metafield if provided, otherwise calculate it
    let discountPercentage;
    if (discountPercentageOverride !== null) {
      discountPercentage = discountPercentageOverride;
      console.log('[Personalized Bundles] Using discount percentage from metafield:', discountPercentage + '%');
    } else {
      discountPercentage = fullSubtotal > 0 ? ((fullSubtotal - bundlePrice) / fullSubtotal) * 100 : 0;
      console.log('[Personalized Bundles] Calculated discount percentage:', discountPercentage.toFixed(2) + '%');
    }

    // Calculate new subtotal (only unowned items)
    const newSubtotal = unownedItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return sum + (price * quantity);
    }, 0);

    // Apply discount percentage to each item individually (matching Shopify's calculation)
    // Shopify rounds each line item discount to cents
    console.log('[Personalized Bundles] Calculating per-item discounts:');
    const personalizedDiscount = unownedItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      const itemSubtotal = price * quantity;
      // Apply percentage discount and round to cents (matching Shopify)
      const itemDiscount = Math.round(itemSubtotal * (discountPercentage / 100) * 100) / 100;
      console.log(`  ${item.title}: $${itemSubtotal.toFixed(2)} × ${discountPercentage.toFixed(2)}% = -$${itemDiscount.toFixed(2)}`);
      return sum + itemDiscount;
    }, 0);

    const personalizedPrice = newSubtotal - personalizedDiscount;
    
    console.log('[Personalized Bundles] Final calculation:');
    console.log(`  Subtotal: $${newSubtotal.toFixed(2)}`);
    console.log(`  Discount: -$${personalizedDiscount.toFixed(2)}`);
    console.log(`  Total: $${personalizedPrice.toFixed(2)}`);

    return {
      fullSubtotal,
      newSubtotal,
      personalizedPrice,
      personalizedDiscount,
      discountPercentage,
      ownedCount: ownedItems.length,
      unownedCount: unownedItems.length,
      totalCount: bundleItems.length
    };
  }

  /**
   * Update bundle items display to show "Purchased" for owned items
   */
  async function updateBundleItemsDisplay() {
    const bundleCards = document.querySelectorAll('.bundle-card-item');
    
    if (bundleCards.length === 0) return;

    const { handles } = await getOwnedProductIdentifiers();

    if (handles.length === 0) {
      console.log('[Personalized Bundles] No owned products found');
      return;
    }

    console.log('[Personalized Bundles] Checking bundle cards against owned handles:', handles);

    bundleCards.forEach(card => {
      const productHandle = card.dataset.productHandle;
      
      console.log('[Personalized Bundles] Checking card with handle:', productHandle);
      
      const isOwned = productHandle && handles.includes(productHandle);

      if (isOwned) {
        console.log('[Personalized Bundles] Marking as owned:', productHandle);
        
        // Mark card as owned
        card.classList.add('owned-product');
        
        // Replace price with "Purchased"
        const priceElement = card.querySelector('.amount');
        if (priceElement) {
          priceElement.innerHTML = '<span class="font-weight-bold">Purchased</span>';
        }

        // Add visual indicator
        card.style.opacity = '0.6';
      }
    });
  }

  /**
   * Update bundle pricing display
   */
  async function updateBundlePricing() {
    const pricingSection = document.querySelector('.bundle-content-total');
    if (!pricingSection) {
      console.log('[Personalized Bundles] No pricing section found');
      return;
    }

    // Collect bundle item data
    const bundleCards = document.querySelectorAll('.bundle-card-item');
    const bundleItems = Array.from(bundleCards).map(card => ({
      productId: card.dataset.productId,
      handle: card.dataset.productHandle,
      price: parseFloat(card.dataset.price) || 0,
      quantity: parseInt(card.dataset.quantity) || 1
    }));

    console.log('[Personalized Bundles] Bundle items:', bundleItems);

    if (bundleItems.length === 0) {
      console.log('[Personalized Bundles] No bundle items found');
      return;
    }

    // Get bundle price from page
    const bundlePrice = parseFloat(pricingSection.dataset.bundlePrice) || 0;
    
    console.log('[Personalized Bundles] Bundle price from page:', bundlePrice);
    
    if (bundlePrice === 0) {
      console.log('[Personalized Bundles] Bundle price is 0, skipping');
      return;
    }

    // Get discount percentage from metafield if available
    const discountPercentage = pricingSection.dataset.discountPercentage 
      ? parseFloat(pricingSection.dataset.discountPercentage) 
      : null;

    // Process items to mark which are owned
    const processedItems = await processBundleItems(bundleItems);
    
    console.log('[Personalized Bundles] Processed items:', processedItems);
    
    // Calculate personalized pricing
    const pricing = calculatePersonalizedPricing(processedItems, bundlePrice, discountPercentage);

    console.log('[Personalized Bundles] Calculated pricing:', pricing);

    // Helper function to format money (input is in dollars)
    function formatMoney(dollars) {
      return '$' + parseFloat(dollars).toFixed(2);
    }

    // Update the pricing display
    const subtotalElement = pricingSection.querySelector('[data-subtotal]');
    const discountElement = pricingSection.querySelector('[data-discount]');
    const totalElement = pricingSection.querySelector('[data-total]');

    if (pricing.ownedCount > 0) {
      if (subtotalElement) {
        subtotalElement.textContent = formatMoney(pricing.newSubtotal);
      }
      if (discountElement) {
        discountElement.textContent = '-' + formatMoney(pricing.personalizedDiscount);
      }
      if (totalElement) {
        totalElement.textContent = formatMoney(pricing.personalizedPrice);
      }
      
      // Also update product banner and sticky bar pricing
      updateBannerPricing(pricing);
      updateStickyBarPricing(pricing);
    }
  }
  
  /**
   * Update product banner pricing display
   */
  function updateBannerPricing(pricing) {
    const bannerPricing = document.querySelector('[data-bundle-pricing]');
    if (!bannerPricing) return;
    
    const bannerSubtotal = bannerPricing.querySelector('[data-banner-subtotal]');
    const bannerTotal = bannerPricing.querySelector('[data-banner-total]');
    
    // Helper function to format money (input is in dollars)
    function formatMoney(dollars) {
      return '$' + parseFloat(dollars).toFixed(2).replace(/\.00$/, '');
    }
    
    if (pricing.ownedCount > 0) {
      if (bannerSubtotal) {
        bannerSubtotal.textContent = formatMoney(pricing.newSubtotal);
      }
      if (bannerTotal) {
        bannerTotal.textContent = formatMoney(pricing.personalizedPrice);
      }
      
      console.log('[Personalized Bundles] Updated banner pricing');
    }
  }
  
  /**
   * Update sticky bar pricing display
   */
  function updateStickyBarPricing(pricing) {
    const stickySubtotal = document.querySelector('[data-sticky-subtotal]');
    const stickyTotal = document.querySelector('[data-sticky-total]');
    
    // Helper function to format money (input is in dollars)
    function formatMoney(dollars) {
      return '$' + parseFloat(dollars).toFixed(2).replace(/\.00$/, '');
    }
    
    if (pricing.ownedCount > 0) {
      if (stickySubtotal) {
        stickySubtotal.textContent = formatMoney(pricing.newSubtotal);
      }
      if (stickyTotal) {
        stickyTotal.textContent = formatMoney(pricing.personalizedPrice);
      }
      
      console.log('[Personalized Bundles] Updated sticky bar pricing');
    }
  }

  /**
   * Update bundle button prices on collection/archive pages
   */
  async function updateBundleButtonPrices() {
    const bundleButtons = document.querySelectorAll('[data-bundle-button-price]');
    
    if (bundleButtons.length === 0) {
      return;
    }

    console.log('[Personalized Bundles] Found', bundleButtons.length, 'bundle buttons to update');

    // Get owned product handles
    const { handles } = await getOwnedProductIdentifiers();
    
    if (handles.length === 0) {
      console.log('[Personalized Bundles] No owned products, keeping original button prices');
      return;
    }

    console.log('[Personalized Bundles] Owned handles for buttons:', handles);

    // For each bundle button, calculate personalized price
    for (const button of bundleButtons) {
      const productHandle = button.dataset.productHandle;
      const bundlePrice = parseFloat(button.dataset.bundlePrice) || 0;
      const discountPercentage = parseFloat(button.dataset.discountPercentage) || 0;
      const priceSpan = button.querySelector('.button-price');

      if (!priceSpan) continue;

      try {
        // Parse bundle items from data attribute
        const bundleItemsData = button.dataset.bundleItems;
        if (!bundleItemsData) {
          continue;
        }

        const bundleItems = JSON.parse(bundleItemsData);
        
        // Calculate subtotal of unowned items (prices are in cents)
        let unownedSubtotal = 0;
        let ownedCount = 0;
        
        bundleItems.forEach(item => {
          const isOwned = handles.includes(item.handle);
          if (!isOwned) {
            // Convert cents to dollars
            const priceInDollars = item.price / 100;
            unownedSubtotal += priceInDollars * item.quantity;
          } else {
            ownedCount++;
          }
        });

        // Calculate personalized price
        const personalizedPrice = Math.round(unownedSubtotal * (discountPercentage / 100) * 100) / 100;
        const finalPrice = unownedSubtotal - personalizedPrice;
        
        if (ownedCount > 0 && finalPrice > 0) {
          // Format price with cents
          const priceFormatted = '$' + finalPrice.toFixed(2).replace(/\.00$/, '');
          priceSpan.textContent = priceFormatted;
          console.log(`[Personalized Bundles] Button ${productHandle}: ${priceFormatted} (owned: ${ownedCount}/${bundleItems.length})`);
        }

      } catch (error) {
        console.error('[Personalized Bundles] Error updating button:', productHandle, error);
      }
    }
  }

  /**
   * Update bundle card savings on collection/archive pages
   */
  async function updateBundleCardSavings() {
    const savingsCards = document.querySelectorAll('[data-bundle-card-savings]');
    
    if (savingsCards.length === 0) {
      return;
    }

    console.log('[Personalized Bundles] Found', savingsCards.length, 'bundle cards to update');

    // Get owned product handles
    const { handles } = await getOwnedProductIdentifiers();
    
    if (handles.length === 0) {
      console.log('[Personalized Bundles] No owned products, showing fallback text');
      // Show fallback text for all cards
      savingsCards.forEach(card => {
        const fallbackText = card.dataset.fallbackText || 'SAVE';
        const amountSpan = card.querySelector('.savings-amount');
        if (amountSpan) amountSpan.textContent = fallbackText;
      });
      return;
    }

    console.log('[Personalized Bundles] Owned handles:', handles);

    // For each bundle card, calculate savings based on unowned items
    for (const card of savingsCards) {
      const productHandle = card.dataset.productHandle;
      const discountPercentage = parseFloat(card.dataset.discountPercentage) || 0;
      const fallbackText = card.dataset.fallbackText || 'SAVE';
      const amountSpan = card.querySelector('.savings-amount');

      if (!amountSpan) continue;

      try {
        // Parse bundle items from data attribute
        const bundleItemsData = card.dataset.bundleItems;
        if (!bundleItemsData) {
          amountSpan.textContent = fallbackText;
          continue;
        }

        const bundleItems = JSON.parse(bundleItemsData);
        
        // Calculate subtotal of unowned items (prices are in cents)
        let unownedSubtotal = 0;
        let ownedCount = 0;
        
        bundleItems.forEach(item => {
          const isOwned = handles.includes(item.handle);
          if (!isOwned) {
            // Convert cents to dollars
            const priceInDollars = item.price / 100;
            unownedSubtotal += priceInDollars * item.quantity;
          } else {
            ownedCount++;
          }
        });

        // Calculate savings
        const savings = Math.round(unownedSubtotal * (discountPercentage / 100) * 100) / 100;
        
        if (savings > 0) {
          // Format savings with cents and exclamation point
          const savingsFormatted = savings.toFixed(2).replace(/\.00$/, '');
          amountSpan.textContent = `SAVE $${savingsFormatted}!`;
          console.log(`[Personalized Bundles] Card ${productHandle}: SAVE $${savingsFormatted}! (owned: ${ownedCount}/${bundleItems.length})`);
        } else {
          // User owns everything in the bundle
          amountSpan.textContent = 'OWNED';
          console.log(`[Personalized Bundles] Card ${productHandle}: All items owned`);
        }

      } catch (error) {
        console.error('[Personalized Bundles] Error updating card:', productHandle, error);
        amountSpan.textContent = fallbackText;
      }
    }
  }

  /**
   * Initialize personalized bundles
   */
  async function init() {
    console.log('[Personalized Bundles] Initializing...');
    console.log('[Personalized Bundles] Customer object:', window.Shopify?.customer);
    console.log('[Personalized Bundles] Customer ID from window:', window.shopifyCustomerId);

    // Check if this is a product page with bundle items
    const bundleItems = document.querySelectorAll('.bundle-card-item');
    const hasBundleItems = bundleItems.length > 0;
    
    // Check if this is a collection page with bundle cards
    const bundleCardSavings = document.querySelectorAll('[data-bundle-card-savings]');
    const hasBundleCards = bundleCardSavings.length > 0;
    
    // Check for bundle buttons with prices
    const bundleButtons = document.querySelectorAll('[data-bundle-button-price]');
    const hasBundleButtons = bundleButtons.length > 0;

    console.log('[Personalized Bundles] Found bundle items:', bundleItems.length);
    console.log('[Personalized Bundles] Found bundle card savings:', bundleCardSavings.length);
    console.log('[Personalized Bundles] Found bundle buttons:', bundleButtons.length);

    if (!hasBundleItems && !hasBundleCards && !hasBundleButtons) {
      console.log('[Personalized Bundles] No bundle content found on page');
      // Retry after a short delay in case DOM is still loading
      setTimeout(async () => {
        const retryBundleCards = document.querySelectorAll('[data-bundle-card-savings]');
        const retryBundleButtons = document.querySelectorAll('[data-bundle-button-price]');
        if (retryBundleCards.length > 0) {
          console.log('[Personalized Bundles] Found bundle cards on retry:', retryBundleCards.length);
          await updateBundleCardSavings();
        }
        if (retryBundleButtons.length > 0) {
          console.log('[Personalized Bundles] Found bundle buttons on retry:', retryBundleButtons.length);
          await updateBundleButtonPrices();
        }
      }, 500);
      return;
    }

    try {
      if (hasBundleItems) {
        // Product page updates
        await updateBundleItemsDisplay();
        await updateBundlePricing();
      }
      
      if (hasBundleCards) {
        // Collection page updates
        await updateBundleCardSavings();
      }
      
      if (hasBundleButtons) {
        // Update bundle button prices
        await updateBundleButtonPrices();
      }
      
      console.log('[Personalized Bundles] Initialization complete');
    } catch (error) {
      console.error('[Personalized Bundles] Initialization error:', error);
    }
  }

  // Export public API
  window.PersonalizedBundles = {
    init,
    fetchOwnedProducts,
    getOwnedProductIdentifiers,
    isProductOwned,
    processBundleItems,
    calculatePersonalizedPricing,
    updateBundleItemsDisplay,
    updateBundlePricing,
    updateBundleCardSavings,
    updateBundleButtonPrices
  };

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
